import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Table from '../common/Table';
import RTFilters from '../common/RTFilters';
import RTFilteredTable from '../common/RTFilteredTable';
import RTButton from '../common/RTButton';
import { apiGet } from '../../api/apiClient';
import { navigateToPath } from '../../utils/navigationHelper';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import { OCCUPANCY_GENDER_FILTER_OPTIONS } from '../../utils/occupancyConstants';
import './ViewAllocatedBeds.css';
import RTPageLayout from '../common/RTPageLayout';

const ViewSoonVacatingBeds = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [allocations, setAllocations] = useState([]);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ occupancy_gender: undefined });

  const filterFields = useMemo(() => [
    {
      key: 'occupancy_gender',
      label: 'Gender',
      type: 'dropdown',
      width: 'compact',
      options: OCCUPANCY_GENDER_FILTER_OPTIONS,
      applyImmediately: true,
    },
  ], []);

  useEffect(() => {
    fetchData(filters);
  }, []);

  const fetchData = async (filterParams = filters) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filterParams.occupancy_gender) {
        params.append('occupancy_gender', filterParams.occupancy_gender);
      }
      const query = params.toString();
      const url = query ? `/allocation/soon-vacating?${query}` : '/allocation/soon-vacating';
      const allocationResponse = await apiGet(url);
      setAllocations(allocationResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load soon vacating beds data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value || undefined };
    setFilters(next);
    fetchData(next);
  };

  const appliedFilterPills = useMemo(() => {
    if (!filters.occupancy_gender) return [];
    const genderLabel = OCCUPANCY_GENDER_FILTER_OPTIONS.find(
      o => o.value === filters.occupancy_gender
    )?.label || filters.occupancy_gender;
    return [{ key: 'occupancy_gender', label: `Gender: ${genderLabel}` }];
  }, [filters]);

  const handleRemoveAppliedFilter = (key) => {
    const next = { ...filters, [key]: undefined };
    setFilters(next);
    fetchData(next);
  };

  const handleClearAllAppliedFilters = () => {
    const next = { occupancy_gender: undefined };
    setFilters(next);
    fetchData(next);
  };

  const handlePGClick = (pgId) => {
    window.open(`/paying-guest/view/${pgId}`, '_blank');
  };

  const handleBuildingClick = (buildingId) => {
    window.open(`/building/view/${buildingId}`, '_blank');
  };

  const handleViewAllocation = (allocationId) => {
    navigateToPath(`/bedallocation/allocation/${allocationId}`);
  };

  const columns = [
    {
      Header: 'Days Until Vacating',
      accessor: 'days_until_vacation',
      Cell: ({ value }) => (
        <span className={value <= 7 ? 'urgent-vacation' : value <= 15 ? 'warning-vacation' : ''}>
          {value} days
        </span>
      )
    },
    {
      Header: 'Start Date',
      accessor: 'start_date',
      Cell: ({ value }) => formatDateToDDMMYYYY(value)
    },
    {
      Header: 'End Date',
      accessor: 'end_date',
      Cell: ({ value }) => formatDateToDDMMYYYY(value)
    },
    {
      Header: 'Notice Date',
      accessor: 'notice_date',
      Cell: ({ value }) => value ? formatDateToDDMMYYYY(value) : 'N/A'
    },
    {
      Header: 'PG Name',
      accessor: 'pg_name',
      Cell: ({ value, row }) => (
        <RTButton
          type="link"
          onClick={() => handlePGClick(row.pg_id)}
          className="rt-link-button"
        >
          {value || 'N/A'}
        </RTButton>
      )
    },
    {
      Header: 'Building',
      accessor: 'building_name',
      Cell: ({ value, row }) => (
        <RTButton
          type="link"
          onClick={() => handleBuildingClick(row.building_id)}
          className="rt-link-button"
        >
          {value || 'N/A'}
        </RTButton>
      )
    },
    {
      Header: 'Room',
      accessor: 'room_name'
    },
    {
      Header: 'Rent Amount',
      accessor: 'rent_amount',
      Cell: ({ value }) => value ? `₹${value.toLocaleString()}` : 'N/A'
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <RTButton
          type="link"
          onClick={() => handleViewAllocation(row.allocation_id)}
          style={{ fontWeight: 'bold' }}
        >
          View
        </RTButton>
      )
    }
  ];

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="Vacating Soon"
      actions={<>
        <RTButton variant="secondary" onClick={() => window.history.back()}>
          &#8592; Back
        </RTButton>
      </>}
    >



      {error && (
        <div className="rt-error-message">
          {error}
          <RTButton
            type="primary"
            onClick={() => fetchData(filters)}
            style={{ marginLeft: '10px' }}
          >
            Retry
          </RTButton>
        </div>
      )}

      <RTFilteredTable
        loading={loading}
        loadingMessage="Loading soon vacating beds data..."
        filters={
          <RTFilters
            fields={filterFields}
            values={filters}
            onChange={handleFilterChange}
            pills={appliedFilterPills}
            onRemovePill={handleRemoveAppliedFilter}
            onClearAll={handleClearAllAppliedFilters}
          />
        }
      >
        <Table
          title={`Soon Vacating Beds Details (${allocations.length} Total)`}
          columns={columns}
          data={allocations}
          showSearch={true}
          exportable={true}
          showPagination={true}
          noDataText="No beds are vacating in the next 30 days."
          pageSize={20}
        />
      </RTFilteredTable>
    </RTPageLayout>
  );
};

export default ViewSoonVacatingBeds;
